// Caller types on Call Logs rows (contract ~/pm/contracts/B4.md v1.2 §1, CU-869fm5hhm).
// Pure (no React / MUI) so it is testable without the components barrel.
import { callLineLabel, serverLineLabel } from 'src/utils/callLineLabel';

// `caller_label` → chip text and theme palette key.
export const CALLER_LABEL = {
  patient: { key: 'patient', label: 'Patient', color: 'success' },
  archived_patient: { key: 'archived_patient', label: 'Archived patient', color: 'warning' },
  shared_number: { key: 'shared_number', label: 'Shared number', color: 'info' },
  // One label for every number not linked to a lead, inbound or outbound (UX-C polish);
  // the direction has its own column.
  new_caller: { key: 'new_caller', label: 'Not a lead yet', color: 'info' },
  not_a_patient: { key: 'not_a_patient', label: 'Not a patient', color: 'grey' },
  withheld: { key: 'withheld', label: 'Withheld', color: 'grey' },
};
// Caller filter options (`?caller=`); `spam` filters on the Elevate spam hint.
export const CALLER_FILTER_OPTIONS = [
  ...Object.values(CALLER_LABEL).map((c) => [c.key, c.label]),
  ['spam', 'Likely spam'],
];
export const callerLabelText = (key) => CALLER_LABEL[key]?.label || (key ? String(key).replace(/_/g, ' ') : '');

export const isOutbound = (row) => typeof row?.direction === 'string' && row.direction.startsWith('outbound');
export function callerLabelMeta(row) {
  // The backend labels every unlinked number "new_caller", including numbers we rang:
  // both read "Not a lead yet" (the direction column says which way the call went).
  return CALLER_LABEL[row?.callerLabel] || null;
}

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');

// Row fields from the list payload. All empty / null on an older backend, so the
// UI shows nothing new until B4 is live.
export function callerFieldsOf(raw) {
  const hints = raw?.caller_hints && typeof raw.caller_hints === 'object' ? raw.caller_hints : {};
  const flag = hints.flag && typeof hints.flag === 'object' ? hints.flag : null;
  return {
    callerLabel: CALLER_LABEL[raw?.caller_label] ? raw.caller_label : '',
    callerHints: {
      displayName: str(hints.display_name),
      spam: hints.spam === true,
      spamText: str(hints.spam_text),
      verifiedCaller: str(hints.verified_caller),
      ringGroup: str(hints.ring_group),
      addressBookName: str(hints.address_book_name),
      flag: flag ? {
        id: flag.id, label: str(flag.label), createdAt: flag.created_at || null, createdByName: str(flag.created_by_name), undoUntil: flag.undo_until || null,
      } : null,
    },
    callerCandidates: Array.isArray(raw?.caller_candidates) ? raw.caller_candidates.filter((c) => c && c.id != null) : [],
    followUpResult: str(raw?.follow_up_result),
  };
}

const same = (a, b) => Boolean(a) && Boolean(b) && a.trim().toLowerCase() === b.trim().toLowerCase();
const startsWithWord = (text, prefix) => Boolean(text) && Boolean(prefix)
  && text.toLowerCase().startsWith(`${prefix.toLowerCase()} `);

// On inbound calls Elevate's `displayName` is sometimes the ring group the call came
// in on ("Wokingham Existing") and sometimes the caller's name. It is read as the
// line only when it matches the ring group or the site (or starts with the site's
// name); anything else stays the caller ID, because the line text is also written
// into a new lead's note.
export function displayNameIsLine(row) {
  const h = row?.callerHints || {};
  if (!h.displayName || isOutbound(row)) return false;
  const ringGroup = h.ringGroup || row?.huntGroup || '';
  const site = row?.siteLabel || '';
  return same(h.displayName, ringGroup) || same(h.displayName, site) || startsWithWord(h.displayName, site);
}

// The call's line, the same label everywhere (utils/callLineLabel): "Wokingham · Existing",
// without repeating the site or showing an internal connection name, else the number rung.
export function lineText(row) {
  const h = row?.callerHints || {};
  const ringGroup = h.ringGroup || row?.huntGroup || (displayNameIsLine(row) ? h.displayName : '');
  return callLineLabel({ site: row?.siteLabel || '', ringGroup, siteNumber: row?.siteNumber || '' });
}

// The Line column: the server's line name and number ("main · 01908 731705 · Existing",
// CU-869fn2wcn). '' when the backend sent no `line_label` (older backend): the column then
// shows lineText as before.
export function lineColumnText(row) {
  const h = row?.callerHints || {};
  const ringGroup = h.ringGroup || row?.huntGroup || (displayNameIsLine(row) ? h.displayName : '');
  return serverLineLabel(row?.lineLabel, ringGroup);
}

// One short line of hints under the number, most useful first. The network
// name is skipped when it only repeats the contact-book name, and when it is
// really the line (see displayNameIsLine); outbound rows have no caller ID.
export function callerHintParts(row) {
  const h = row?.callerHints || {};
  const parts = [];
  if (h.flag?.label) parts.push({ key: 'flag', text: h.flag.label });
  if (h.addressBookName) parts.push({ key: 'book', text: `Elevate contact: ${h.addressBookName}` });
  if (h.displayName && !isOutbound(row) && !displayNameIsLine(row) && !same(h.displayName, h.addressBookName)) {
    parts.push({ key: 'network', text: `Caller ID: ${h.displayName}` });
  }
  if (h.spam) parts.push({ key: 'spam', text: 'Likely spam', tone: 'error' });
  // Only when there is a ring group to name; the site alone has its own column.
  const line = h.ringGroup || row?.huntGroup || displayNameIsLine(row) ? lineText(row) : '';
  if (line) parts.push({ key: 'line', text: `Line: ${line}` });
  if (row?.followUpResult === 'couldnt_reach') parts.push({ key: 'reach', text: "Couldn't reach after 3 tries", tone: 'warning' });
  return parts;
}

const candidateName = (c) => [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || `Lead ${c.id}`;

// The archived lead a row points at: the linked lead, else the only candidate.
export function archivedLeadOf(row) {
  if (row?.lead?.id && row.lead.isArchived) return { id: row.lead.id, name: row.lead.name, archiveReason: row.lead.archiveReason || '' };
  const only = row?.callerCandidates?.length === 1 ? row.callerCandidates[0] : null;
  return only ? { id: only.id, name: candidateName(only), archiveReason: only.archive_reason || '', status: only.status || '' } : null;
}

export const candidateOptions = (row) => (row?.callerCandidates || []).map((c) => ({
  id: c.id,
  name: candidateName(c),
  archived: c.is_archived === true,
  detail: [c.is_archived ? 'Archived' : '', c.status ? String(c.status).replace(/_/g, ' ').toLowerCase() : '', c.archive_reason || ''].filter(Boolean).join(' · '),
}));

// A row whose line isn't set up (B3 recording_reason) gets "Ask an admin" / "Set up line".
export const lineNotSetUp = (row) => row?.recordingReason === 'line_not_set_up';
